import { Box, Typography, IconButton, Chip, Grid, Card, CardContent, Divider, CircularProgress, Alert, Stack } from "@mui/material";
import { Close as CloseIcon, CalendarToday, Person, Inventory } from "@mui/icons-material";
import { formatDate } from "../../../shared/utils/formatDate";
import { useGetResourceReqDetails } from "../hooks/useGetResourceReqDetails";

export const DetailItem = ({ label, value }: { label: string; value: string | number | null | undefined }) => (
    <Box display="flex" justifyContent="space-between" alignItems="flex-start" py={0.75}>
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="body2" sx={{ textAlign: 'right', maxWidth: '60%' }}>
            {value || 'N/A'}
        </Typography>
    </Box>
);

export const RequestDetailsHeader = ({
    projectId,
    requestId,
    onClose
}: {
    projectId: string | undefined;
    requestId: string | null;
    onClose: () => void;
}) => {

    const { data, isFetching, isPending } = useGetResourceReqDetails({ projectId, requestId });

    const getStatusColor = (status: string) => {
        switch (status?.toLowerCase().replace(/_/g, ' ')) {
            case 'order placed':
                return 'primary';
            case 'delivered':
                return 'success';
            case 'pending':
                return 'warning';
            case 'cancelled':
                return 'error';
            default:
                return 'default';
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2.5,
                borderBottom: 1,
                borderColor: 'divider',
                width: '100%',
                bgcolor: 'background.paper',
            }}
        >
            <Box sx={{ flex: 1 }}>
                {(isFetching || isPending) ? (
                    <Stack direction="row" spacing={2} alignItems="center">
                        <CircularProgress size={20} />
                        <Typography variant="body1">Loading request details...</Typography>
                    </Stack>
                ) : (
                    <Stack spacing={1}>
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                            {data?.material_ref}
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                            <Chip
                                label={data?.type_display_text || 'N/A'}
                                size="small"
                                variant="outlined"
                            />
                            <Chip
                                label={data?.status_display_text || 'N/A'}
                                color={getStatusColor(data?.status || '')}
                                size="small"
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Person sx={{ fontSize: 14 }} />
                                {data?.owner_name || 'N/A'}
                            </Typography>
                        </Stack>
                    </Stack>
                )}
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ ml: 2 }}>
                <CloseIcon />
            </IconButton>
        </Box>
    );

};

export const RequestDetails = ({ projectId, requestId }: { projectId: string | undefined, requestId: string | null }) => {

    const { data, isFetching, isPending, isError, error } = useGetResourceReqDetails({ projectId, requestId });

    if (isFetching || isPending) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, p: 4 }}>
                <Stack spacing={2} alignItems="center">
                    <CircularProgress size={40} />
                    <Typography variant="body2" color="text.secondary">Loading request details...</Typography>
                </Stack>
            </Box>
        );
    }

    if (isError) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" variant="filled">
                    {error?.message || 'Failed to load resource tracking details'}
                </Alert>
            </Box>
        );
    }

    if (!data) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="info" variant="outlined">No data available for this request</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ bgcolor: 'background.default' }}>

            {/* General Information */}
            <Card elevation={0} sx={{ borderRadius: 2 }}>
                <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                        <Inventory color="primary" sx={{ fontSize: 20 }} />
                        <Typography variant="subtitle1">General Information</Typography>
                    </Stack>
                    <Divider sx={{ mb: 1 }} />
                    <Stack>
                        <DetailItem label="Resource Reference" value={data.material_ref} />
                        <DetailItem label="Description" value={data.description} />
                        <DetailItem label="Duration" value={data.quantity} />
                    </Stack>
                    <Typography variant="subtitle1" my={1}>Project Details</Typography>
                    <Divider sx={{ mb: 1 }} />
                    <Stack spacing={0.5}>
                        <DetailItem label="Project" value={data.project_title} />
                        <DetailItem label="Phase" value={data.phase_name} />
                        <DetailItem label="Activity" value={data.activity_name} />
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center" my={1}>
                        <CalendarToday color="primary" sx={{ fontSize: 20 }} />
                        <Typography variant="subtitle1">Supplier & Delivery</Typography>
                    </Stack>
                    <Divider sx={{ mb: 1 }} />
                    <Stack spacing={0.5}>
                        <DetailItem label="Supplier" value={data.supplier_display_text} />
                        <DetailItem label="Point of Contact" value={data.point_of_contact_info} />
                        <Divider sx={{ my: 1 }} />
                        <DetailItem label="Required Date" value={formatDate(data.required_date)} />
                        <DetailItem label="Order Placed Date" value={formatDate(data.order_placed_date)} />
                        <DetailItem label="Est. Delivery Date" value={formatDate(data.est_delivery_date)} />
                        <Divider sx={{ my: 1 }} />
                        <DetailItem label="Created Date" value={formatDate(data.created_date)} />
                    </Stack>
                    <Typography variant="subtitle1" my={1}>Additional Information</Typography>
                    <Divider sx={{ mb: 1 }} />
                    <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={500} display="block" mb={1}>
                            Last Updated Comments
                        </Typography>
                        <Typography variant="body2">
                            {data.last_updated_comments || 'No comments available'}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>

        </Box>
    );

};